/**
 * Marketing Page
 *
 * Public page - accessible without authentication. Used as the shared Marketing
 * URL for the CCMS apps on the Apple App Store and Google Play. A landing-style
 * overview of the whole CCMS app suite. Not linked from the login footer.
 */

import { Link } from 'react-router-dom';
import {
  Box,
  Stack,
  Group,
  SimpleGrid,
  Card,
  ThemeIcon,
  Title,
  Text,
  Badge,
  Button,
  Image,
  Anchor,
  rem,
} from '@mantine/core';
import {
  IconListCheck,
  IconUsers,
  IconCalendarEvent,
  IconShoppingCart,
  IconSchool,
  IconMessages,
  IconKey,
  IconBell,
  IconSparkles,
  IconDeviceMobile,
  IconAccessible,
  IconShieldLock,
  IconBrandApple,
  IconBrandAndroid,
  IconArrowRight,
} from '@tabler/icons-react';
import PublicPageLayout from './PublicPageLayout';

/** The apps in the CCMS suite (those published for iPhone & Android). */
const APPS = [
  {
    icon: IconListCheck,
    name: 'CCMS Tasks',
    text: "Plan, assign and track your team's work — with AI to prioritise what matters most.",
  },
  {
    icon: IconUsers,
    name: 'CCMS HR',
    text: 'Leave, policies and duties — staff self-service in your pocket.',
  },
  {
    icon: IconCalendarEvent,
    name: 'CCMS Bookings',
    text: 'Book venues, vehicles and resources, and keep track of how they’re used.',
  },
  {
    icon: IconShoppingCart,
    name: 'CCMS Procurement',
    text: 'Raise requests, get approvals and manage purchase orders on the go.',
  },
  {
    icon: IconSchool,
    name: 'CCMS Resources',
    text: 'Courses, a knowledge base and learning for your whole team.',
  },
  {
    icon: IconMessages,
    name: 'CCMS Chat',
    text: 'Message your team, share voice notes and make calls — all in one place.',
  },
];

/** Strengths shared across every app in the suite. */
const PLATFORM = [
  {
    icon: IconKey,
    title: 'One secure sign-in',
    text: 'Sign in once and move between your CCMS apps — no re-typing passwords.',
  },
  {
    icon: IconBell,
    title: 'Real-time notifications',
    text: 'A shared inbox and push alerts keep you across everything that matters.',
  },
  {
    icon: IconSparkles,
    title: 'AI where it helps',
    text: 'Smart assistance built into the apps to save you time and effort.',
  },
  {
    icon: IconDeviceMobile,
    title: 'iPhone, iPad & Android',
    text: 'Your work on every device, always kept in sync.',
  },
  {
    icon: IconAccessible,
    title: 'Accessible by design',
    text: 'VoiceOver, TalkBack, Dynamic Type and in-app accessibility controls.',
  },
  {
    icon: IconShieldLock,
    title: 'Private & secure',
    text: 'Your data belongs to your organisation, protected by sign-in and Face ID.',
  },
];

export default function MarketingPage() {
  return (
    <PublicPageLayout
      eyebrow="CCMS Apps"
      title="Apps that help run your ministry, together"
      subtitle="A family of apps for Acts Christian Church teams — manage tasks, people, bookings, procurement and learning, all with one secure sign-in."
      actions={
        <>
          <Button component={Link} to="/auth/login" radius="xl" size="md" variant="white" color="dark">
            Sign in
          </Button>
          <Button
            component={Link}
            to="/support"
            radius="xl"
            size="md"
            variant="outline"
            rightSection={<IconArrowRight size={16} />}
            styles={{ root: { borderColor: 'rgba(255,255,255,0.5)', color: '#fff' } }}
          >
            Get support
          </Button>
        </>
      }
      art={
        <Image
          src="/illustrations/ccms-tasks-hero.svg"
          alt="Preview of a CCMS app"
          w="100%"
          maw={rem(460)}
          fit="contain"
        />
      }
    >
      {/* Meet the apps */}
      <Stack gap="lg">
        <Stack gap={4} align="center" ta="center">
          <Badge variant="light" radius="sm" size="lg">The suite</Badge>
          <Title order={2} style={{ fontSize: rem(30), letterSpacing: '-0.4px' }}>Meet the CCMS apps</Title>
          <Text c="dimmed" maw={rem(640)}>
            Each app focuses on one part of ministry life — and they all work together, with a single
            account and a shared inbox.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {APPS.map((a) => (
            <Card key={a.name} withBorder radius="lg" padding="lg" shadow="sm">
              <ThemeIcon size={48} radius="md" variant="light">
                <a.icon size={26} />
              </ThemeIcon>
              <Text fw={700} mt="md" size="lg">{a.name}</Text>
              <Text c="dimmed" size="sm" mt={4}>{a.text}</Text>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      {/* One platform */}
      <Stack gap="lg">
        <Stack gap={4} align="center" ta="center">
          <Badge variant="light" radius="sm" size="lg">One platform</Badge>
          <Title order={2} style={{ fontSize: rem(30), letterSpacing: '-0.4px' }}>
            Built to work together
          </Title>
          <Text c="dimmed" maw={rem(640)}>
            However many apps you use, the experience is consistent, connected and secure.
          </Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {PLATFORM.map((f) => (
            <Card key={f.title} withBorder radius="lg" padding="lg" shadow="sm">
              <ThemeIcon size={46} radius="md" variant="light">
                <f.icon size={24} />
              </ThemeIcon>
              <Text fw={700} mt="md">{f.title}</Text>
              <Text c="dimmed" size="sm" mt={4}>{f.text}</Text>
            </Card>
          ))}
        </SimpleGrid>
      </Stack>

      {/* Availability */}
      <Card withBorder radius="lg" padding="xl" bg="var(--mantine-color-indigo-light)">
        <Group justify="space-between" wrap="wrap" gap="lg">
          <Stack gap={4}>
            <Title order={3} style={{ fontSize: rem(24) }}>Available on iPhone &amp; Android</Title>
            <Text c="dimmed" maw={rem(520)}>
              The CCMS apps are provided to members and volunteers of Acts Christian Church. An
              account is required — everything you see belongs to your team and your organisation.
            </Text>
          </Stack>
          <Group gap="sm">
            {[
              { icon: <IconBrandApple size={22} />, label: 'iOS' },
              { icon: <IconBrandAndroid size={22} />, label: 'Android' },
            ].map((p) => (
              <Group
                key={p.label}
                gap={8}
                px="md"
                py={10}
                wrap="nowrap"
                style={{
                  border: '1px solid var(--mantine-color-default-border)',
                  borderRadius: rem(999),
                  background: 'var(--mantine-color-body)',
                }}
              >
                {p.icon}
                <Text fw={600} size="sm">{p.label}</Text>
              </Group>
            ))}
          </Group>
        </Group>
      </Card>

      {/* CTA band */}
      <Box
        style={{
          borderRadius: rem(20),
          padding: rem(40),
          textAlign: 'center',
          background: 'linear-gradient(135deg, #4c6ef5 0%, #7048e8 100%)',
          color: '#fff',
        }}
      >
        <Title order={2} style={{ fontSize: rem(28) }}>Ready to get started?</Title>
        <Text mt="xs" style={{ color: 'rgba(255,255,255,0.9)' }}>
          Sign in to start using the CCMS apps for your team today.
        </Text>
        <Group justify="center" mt="lg">
          <Button component={Link} to="/auth/login" radius="xl" size="md" variant="white" color="dark">
            Sign in
          </Button>
        </Group>
        <Text mt="lg" size="sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
          Questions? See our <Anchor href="/support" c="white" fw={600}>Support</Anchor> page or read our{' '}
          <Anchor href="/privacy" c="white" fw={600}>Privacy Policy</Anchor>.
        </Text>
      </Box>
    </PublicPageLayout>
  );
}
